<template>
  <!--
    Custom Deck builder — Mystic-facing FE for the sample-then-commit
    flow. Phase 6 of CUSTOM_DECK_BUILDER plan.

    Surfaces:
      * Simple (default) — Deck name + Theme combobox + Generation
        prompt base textarea. Two fields that drive output. Aida-led
        in chat, or direct fill.
      * Advanced (toggle) — Same brand-state, but the full 7-field
        combobox grid for power users who want to tune all the
        ingredients themselves.

    Aida-led conversation flow drives both views — the chat-action
    handler upstream applies ``fill_brand_fields`` actions to the
    ``brandState`` ref here so toggling Simple/Advanced never loses
    Aida's input.

    State machine derives from the deck row itself:
      no_draft         → form (start a draft)
      sample_pending   → form filled, "Generate samples (free)" CTA
      samples_visible  → 5-card preview + commit/refresh
      committed        → progress chip, polls until ready
      ready            → "Your deck is ready" + see-deck CTA
  -->
  <div class="custom-deck">
    <header class="header">
      <div v-if="headerTitle || headerSub" class="header-text">
        <h3 v-if="headerTitle" class="title">{{ headerTitle }}</h3>
        <p v-if="headerSub" class="sub">{{ headerSub }}</p>
      </div>
      <!-- Set as Default rendered in the header for the ready phase
           (no extra cell, no duplicate deck-name row). -->
      <button
        v-if="phase === 'ready'"
        class="primary-btn header-cta"
        @click="onPublish"
        :disabled="publishing"
      >{{ publishing ? 'Setting…' : 'Set as Default' }}</button>
    </header>

    <!-- Simple/Advanced mode tabs (only show during fill phases) -->
    <div
      v-if="phase === 'no_draft' || phase === 'sample_pending'"
      class="mode-tabs"
      role="tablist"
    >
      <button
        role="tab"
        type="button"
        class="mode-tab"
        :class="{ active: formMode === 'simple' }"
        :aria-selected="formMode === 'simple'"
        @click="formMode = 'simple'"
      >Simple</button>
      <button
        role="tab"
        type="button"
        class="mode-tab"
        :class="{ active: formMode === 'advanced' }"
        :aria-selected="formMode === 'advanced'"
        @click="formMode = 'advanced'"
      >Advanced</button>
    </div>

    <!-- Phase A: no draft yet → form to start. Help icon above the
         form opens the AI assistant on demand. -->
    <BrandFieldsForm
      v-if="phase === 'no_draft'"
      :modelValue="brandState"
      :presets="presets"
      :mode="formMode"
      :submitting="starting"
      :error="startError"
      submit-label="Start this deck"
      :show-cancel="true"
      :show-help-icon="true"
      @update:modelValue="syncBrandState"
      @submit="onStart"
      @cancel="$emit('cancel')"
    />

    <!-- Phase B: draft exists, samples generating — show 5 placeholders
         that fill in as gpt-image-2 returns. -->
    <div v-else-if="phase === 'sample_pending'" class="sample-pending">
      <SamplePreview
        :cards="sampleCards"
        :generating="true"
        :can-refresh="false"
        subtitle="Five cards generating now (Fool + four Aces). A preview, ~2-4 minutes."
        @open="openSampleLightbox"
      />
      <p v-if="generateError" class="error">⚠ {{ generateError }}</p>

      <div class="start-over-row">
        <button
          class="start-over-link"
          @click="onAbandon"
          :disabled="abandoning"
        >{{ abandoning ? 'Archiving…' : 'Start over (archive this draft)' }}</button>
        <p v-if="abandonError" class="error">⚠ {{ abandonError }}</p>
      </div>
    </div>

    <!-- Phase C: samples generating or all 5 ready — preview + adjust -->
    <div v-else-if="phase === 'samples_visible'" class="samples-visible">
      <SamplePreview
        :cards="sampleCards"
        :generating="!samplesComplete"
        :can-refresh="false"
        @open="openSampleLightbox"
      />

      <!-- Adjust + bulk refresh: editable generation_prompt_base
           textarea + single 50-emerald refresh button. Replaces the
           five per-card 50 ✦ buttons. Operator types adjustments
           directly into the prompt; click to re-gen all 5. -->
      <div class="adjust-block">
        <label class="adjust-label">
          <span>Adjust the brand</span>
          <small>Edit the generation prompt that drives every card. Refresh re-runs the 5 samples with this updated prompt.</small>
        </label>
        <textarea
          v-model="brandState.generation_prompt_base"
          rows="4"
          placeholder="Describe rendering style, palette, lighting, recurring motifs. NO card-specific imagery."
        ></textarea>
        <div class="adjust-row">
          <button
            class="ghost-btn"
            @click="onRefreshSamples"
            :disabled="refreshingSamples || !brandState.generation_prompt_base?.trim()"
          >{{ refreshingSamples ? 'Refreshing…' : 'Refresh all 5 (100 carats)' }}</button>
          <p v-if="refreshError" class="error inline">⚠ {{ refreshError }}</p>
        </div>
      </div>

      <div class="commit-row">
        <button
          class="primary-btn commit-btn"
          @click="onCommit"
          :disabled="!samplesComplete || commitInProgress"
        >{{ commitInProgress ? 'Committing…' : 'Generate full deck (1000 carats)' }}</button>
        <p class="commit-note">
          Charges 1000 ✦. Audit + retry runs in the background.
          You can leave and come back.
        </p>
        <p v-if="commitError" class="error">⚠ {{ commitError }}</p>
      </div>

      <div class="start-over-row">
        <button
          class="start-over-link"
          @click="onAbandon"
          :disabled="abandoning"
        >{{ abandoning ? 'Archiving…' : 'Start over (archive this draft)' }}</button>
        <p v-if="abandonError" class="error">⚠ {{ abandonError }}</p>
      </div>
    </div>

    <!-- Phase D: full-deck gen running — live 78-card grid. Click a
         card to view it larger; refresh button is locked until the
         deck transitions to 'approved'. -->
    <div v-else-if="phase === 'committed'" class="committed">
      <FullDeckGrid
        :cards="sampleCards"
        :deck-name="draft?.name || ''"
        :back-url="draft?.back_image_url"
        :done="false"
        @open="openFullDeckCard"
      />
    </div>

    <!-- Phase E: ready — full grid editable, click any card to refine.
         Set as Default lives in the top header (right-aligned). -->
    <div v-else-if="phase === 'ready'" class="ready">
      <p v-if="publishError" class="error">⚠ {{ publishError }}</p>
      <p v-if="publishSuccess" class="success">✓ Set. Your reader is now using <strong>{{ draft?.name }}</strong>.</p>
      <FullDeckGrid
        :cards="sampleCards"
        :deck-name="draft?.name || ''"
        :back-url="draft?.back_image_url"
        :done="true"
        @open="openFullDeckCard"
      />
    </div>

    <!-- Approved overlay — fires once on the committed → approved
         transition. Two CTAs: Edit (close, stay on this page) or
         Publish (set as active deck). -->
    <div
      v-if="showApprovedOverlay"
      class="approved-overlay"
      @click.self="showApprovedOverlay = false"
    >
      <div class="approved-modal">
        <div class="approved-icon">✦</div>
        <h3>Your deck is ready</h3>
        <p>
          All 78 cards generated. Take a look - refresh any cards
          you want to refine - then publish to use them with your
          reader.
        </p>
        <div class="approved-actions">
          <button class="ghost-btn" @click="showApprovedOverlay = false">
            Edit cards first
          </button>
          <button class="primary-btn" @click="onPublishFromOverlay" :disabled="publishing">
            {{ publishing ? 'Setting…' : 'Set as Default' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Fullscreen sample-card lightbox (sample phase only — view-only) -->
    <SampleLightbox
      v-if="lightboxIdx !== null"
      :card="lightboxCard"
      :cards="filledCards"
      @close="lightboxIdx = null"
      @prev="lightboxIdx = (lightboxIdx - 1 + filledCards.length) % filledCards.length"
      @next="lightboxIdx = (lightboxIdx + 1) % filledCards.length"
    />

    <!-- Per-card lightbox for full-deck phase — image + editable
         prompt + Refresh + version archive. Stays open during regen;
         the image refreshes in place when the new render lands. -->
    <CardLightbox
      v-if="fullDeckCard"
      :card="fullDeckCardLive"
      :deck-slug="draft?.slug || ''"
      :deck-prompt="brandState.generation_prompt_base"
      :editable="phase === 'ready'"
      :refreshing="cardRefreshing"
      :error="cardRefreshError"
      @close="fullDeckCard = null; cardRefreshError = ''"
      @refresh="onCardRefresh"
      @made-live="onCardMadeLive"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import * as api from '@/api-service.js'
import { store } from '@/store'
import BrandFieldsForm from './BrandFieldsForm.vue'
import SamplePreview from './SamplePreview.vue'
import SampleLightbox from './SampleLightbox.vue'
import FullDeckGrid from './FullDeckGrid.vue'
import CardLightbox from './CardLightbox.vue'

const props = defineProps({
  // Optional — when set, load this specific deck instead of the
  // operator's in-flight draft. Used by CustomDeckTab when the
  // operator clicks an existing deck in the gallery. Null = empty
  // form (no_draft).
  slug: { type: String, default: null },
})
defineEmits(['cancel', 'done'])

// ── Shared brand-state — single source of truth ─────────────────
// Both Simple + Advanced tabs bind to this object. Aida's chat-action
// handler (parent ReaderPage) writes into it via the ``fill-brand-fields``
// custom event below. Toggling tabs never loses data.
const brandState = reactive({
  name: '',
  theme: '',
  style: '',
  mood: '',
  color_palette: '',
  motif: '',
  era: '',
  energy: '',
  generation_prompt_base: '',
})

const formMode = ref('simple')
const draft = ref(null)
const status = ref({})
const sampleCards = ref([])
const presets = ref({})

const starting = ref(false)
const startError = ref('')
const generatingSamples = ref(false)
const generateError = ref('')
const refreshingSamples = ref(false)
const refreshError = ref('')
const commitInProgress = ref(false)
const commitError = ref('')
const abandoning = ref(false)
const abandonError = ref('')
const publishing = ref(false)
const publishError = ref('')
const publishSuccess = ref(false)
const showApprovedOverlay = ref(false)
const fullDeckCard = ref(null)
const cardRefreshing = ref(false)
const cardRefreshError = ref('')

function openFullDeckCard(slot) {
  // slot from FullDeckGrid: { name, cardId, imageUrl, inFlight, isBack }
  // Card-back slots have no cardId — open them as view-only (regen
  // wiring needs a reader-side endpoint we haven't surfaced yet).
  if (!slot.imageUrl) return
  if (!slot.isBack && !slot.cardId) return
  fullDeckCard.value = {
    card_id: slot.cardId,
    card_name: slot.name,
    image_url: slot.imageUrl,
    is_back: !!slot.isBack,
  }
  cardRefreshError.value = ''
}

// Computed live-view of the open card: re-derives from sampleCards
// so when polling lands a new image, the modal's image swaps in
// place (no need to close + reopen). For the card-back, image_url
// lives on ``draft.back_image_url`` (not in sampleCards), so we
// re-derive from there.
const fullDeckCardLive = computed(() => {
  if (!fullDeckCard.value) return null
  if (fullDeckCard.value.is_back) {
    return {
      ...fullDeckCard.value,
      image_url: draft.value?.back_image_url || fullDeckCard.value.image_url,
    }
  }
  const fresh = sampleCards.value.find(
    c => c.card_id === fullDeckCard.value.card_id,
  )
  return fresh
    ? { ...fullDeckCard.value, image_url: fresh.image_url }
    : fullDeckCard.value
})

// Watch for the open card's image URL to change — when it does after
// a regen kicks, the new render has landed; flip refreshing off.
watch(
  () => fullDeckCardLive.value?.image_url,
  (next, prev) => {
    if (cardRefreshing.value && next && next !== prev) {
      cardRefreshing.value = false
    }
  },
)

async function onCardRefresh({ cardId, promptOverride }) {
  if (!draft.value) return
  const isBack = !!fullDeckCard.value?.is_back
  if (!isBack && !cardId) return
  cardRefreshing.value = true
  cardRefreshError.value = ''
  try {
    if (isBack) {
      // Back regen has no cardId. Persist any prompt edit onto the
      // deck-wide field BEFORE kicking — mirrors what the card-refresh
      // endpoint does internally for face cards. ``_build_back_prompt``
      // on the BE composes the back prompt off ``generation_prompt_base``
      // + the deck's brand fields.
      const trimmed = (promptOverride || '').trim()
      if (trimmed && trimmed !== (draft.value.generation_prompt_base || '')) {
        brandState.generation_prompt_base = trimmed
      }
      await api.regenerateCustomDeckBack(draft.value.slug)
    } else {
      await api.refreshCustomDeckCard(
        draft.value.slug, cardId, { promptOverride },
      )
    }
    // Modal stays open. Tight polling for the new image — typical
    // gen takes 30-60s, so poll every 4s and let the image swap
    // happen via the watcher above when sampleCards updates.
    if (pollTimer) clearInterval(pollTimer)
    pollTimer = setInterval(refreshStatus, 4000)
    refreshStatus()
  } catch (e) {
    cardRefreshing.value = false
    const data = e?.response?.data
    if (data?.error === 'insufficient_carats') {
      cardRefreshError.value =
        `Not enough carats. You have ${data.balance ?? 0}; refresh costs 50 carats.`
    } else if (data?.error === 'back_regen_locked') {
      cardRefreshError.value =
        data.message || 'Card-back regen is locked while the deck audits.'
    } else {
      cardRefreshError.value = data?.error || data?.message || String(e)
    }
  }
}

function onCardMadeLive({ cardId, isBack, imageUrl }) {
  // Update the open card's image immediately + push the new image
  // into sampleCards so the grid tile under the modal also updates.
  // For the back, write straight into draft.back_image_url so the
  // grid's leading slot (and the lightbox's live-derive) re-render.
  if (isBack) {
    if (fullDeckCard.value?.is_back) {
      fullDeckCard.value = { ...fullDeckCard.value, image_url: imageUrl }
    }
    if (draft.value) {
      draft.value = { ...draft.value, back_image_url: imageUrl }
    }
    return
  }
  if (fullDeckCard.value?.card_id === cardId) {
    fullDeckCard.value = { ...fullDeckCard.value, image_url: imageUrl }
  }
  const idx = sampleCards.value.findIndex(c => c.card_id === cardId)
  if (idx >= 0) {
    sampleCards.value = [
      ...sampleCards.value.slice(0, idx),
      { ...sampleCards.value[idx], image_url: imageUrl },
      ...sampleCards.value.slice(idx + 1),
    ]
  }
}

async function publishDeck() {
  if (!draft.value) return false
  publishing.value = true
  publishError.value = ''
  publishSuccess.value = false
  try {
    // Find the operator's existing tarot variant so we know which
    // variant_key to assign the new deck to. ReaderSkillVariant
    // rows are loaded into store.readerSkillVariants on app boot.
    const tarotVariant = (store.readerSkillVariants || [])
      .find(v => v.skill_key === 'tarot' && v.is_active !== false)
    const variantKey = tarotVariant?.variant_key || 'one_card'
    await api.chooseDeckTemplate(draft.value.slug, {
      skill_key: 'tarot',
      variant_key: variantKey,
    })
    publishSuccess.value = true
    showApprovedOverlay.value = false
    // Refresh variants so the editor's hero/swap-modal pick up the
    // new chosen_deck_template_id without a page reload.
    try {
      const variants = await api.listSkillVariants()
      if (Array.isArray(variants)) store.readerSkillVariants = variants
    } catch { /* non-fatal */ }
    return true
  } catch (e) {
    publishError.value = e?.response?.data?.error || String(e)
    return false
  } finally {
    publishing.value = false
  }
}

async function onPublish() {
  await publishDeck()
}

async function onPublishFromOverlay() {
  const ok = await publishDeck()
  if (ok) showApprovedOverlay.value = false
}

// Lightbox state — index into filledCards (only sample cards with
// images, in canonical sample order). null when closed.
const lightboxIdx = ref(null)
const filledCards = computed(() => sampleCards.value.filter(c => !!c.image_url))
const lightboxCard = computed(() =>
  lightboxIdx.value === null ? null : filledCards.value[lightboxIdx.value],
)
function openSampleLightbox(slot) {
  // slot is the canonical-order entry from SamplePreview; map to its
  // index in filledCards.
  const idx = filledCards.value.findIndex(
    c => c.card_name === slot.cardName,
  )
  if (idx >= 0) lightboxIdx.value = idx
}

let pollTimer = null
const mounted = ref(false)

// ── Phase machine derived from draft state ──────────────────────
const phase = computed(() => {
  if (!mounted.value) return 'loading'
  if (!draft.value) return 'no_draft'
  if (draft.value.creation_phase === 'committed') {
    return status.value?.ready ? 'ready' : 'committed'
  }
  if (draft.value.creation_phase === 'approved') return 'ready'
  // creation_phase === 'sample'
  if ((sampleCards.value?.length || 0) > 0) return 'samples_visible'
  return 'sample_pending'
})

// One-shot trigger: when phase transitions from 'committed' (or
// 'loading' if the operator returns to a freshly-approved deck) into
// 'ready', surface the approved overlay. Don't re-fire if they close
// the overlay and re-poll — track the last-seen phase.
let _previousPhase = null
watch(phase, (next, prev) => {
  if (next === 'ready' && prev === 'committed' && _previousPhase !== 'ready') {
    showApprovedOverlay.value = true
  }
  _previousPhase = next
})

const samplesComplete = computed(() => sampleCards.value.length >= 5)

const headerTitle = computed(() => {
  switch (phase.value) {
    // 'no_draft' intentionally renders no title — the Mystic is greeted
    // by the form + Simple/Advanced tabs, which are explainer enough.
    case 'no_draft': return ''
    case 'sample_pending':
    case 'samples_visible':
    case 'committed':
    case 'ready': return draft.value?.name || 'Your deck'
    default: return 'Custom deck builder'
  }
})
const headerSub = computed(() => {
  switch (phase.value) {
    // 'no_draft' subtitle removed alongside the title — see above.
    case 'no_draft': return ''
    case 'sample_pending':
      return 'Draft saved. Generate samples to preview the brand, or refine the brief.'
    default:
      return ''
  }
})

// ── Aida chat-action listeners (Phase 6) ────────────────────────
// ReaderPage's custom-deck chat handler dispatches these window
// events when the BE Aida returns ``fill_brand_fields`` /
// ``show_button`` actions, OR when the operator clicks one of the
// surfaced trigger buttons in the chat. We listen here so the
// builder updates without prop-drilling through SkillsEditorView.
function _onFillBrandFields(e) {
  const fields = e?.detail?.fields
  if (fields) applyFillBrandFields(fields)
  // If a brand_extract response also created a draft on the BE, the
  // next status refresh picks it up. Refresh now so the FE flips
  // out of no_draft into sample_pending.
  loadDraft()
}
function _onTrigger(e) {
  const key = e?.detail?.key
  if (!key) return
  if (key === 'generate_samples') onGenerateSamples()
  else if (key === 'generate_full_deck') onCommit()
  // refresh_card / use_these_picks are noops here — refresh requires
  // a card_id and use_these_picks is just an Aida acknowledgement.
}

onMounted(async () => {
  window.addEventListener('custom-deck:fill-brand-fields', _onFillBrandFields)
  window.addEventListener('custom-deck:trigger', _onTrigger)
  // Presets only feed the optional THEME / preset suggestion datalists, and
  // the fields fall back to free-text without them — so load them in the
  // BACKGROUND rather than blocking the form's first render on a network
  // round-trip (that await was the load delay).
  api.getCustomDeckBrandPresets()
    .then((data) => { presets.value = data?.presets || {} })
    .catch(() => { presets.value = {} })

  await loadDraft()
  mounted.value = true
})

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer)
  window.removeEventListener('custom-deck:fill-brand-fields', _onFillBrandFields)
  window.removeEventListener('custom-deck:trigger', _onTrigger)
})

// ── Draft state ─────────────────────────────────────────────────
async function loadDraft() {
  // If parent passed a specific slug, load that deck. Otherwise
  // (slug=null), behave like the empty-form entry: no draft loaded,
  // operator fills the form to start a new one. Skip the
  // getCustomDeckDraft auto-resume in that case so "Build new" from
  // the gallery doesn't accidentally resume an in-flight draft.
  let d = null
  if (props.slug) {
    d = await api.getCustomDeckBySlug(props.slug)
  } else {
    d = null  // empty form path
  }
  draft.value = d
  if (d) hydrateBrandStateFromDraft(d)
  if (d) {
    await refreshStatus()
    if (d.creation_phase === 'committed') {
      startPolling()
    } else if (
      d.creation_phase === 'sample' && sampleCards.value.length < 5
    ) {
      // Resume sample-gen polling — operator may have closed the tab
      // mid-generation. Polling will populate as cards land.
      startSamplePolling()
    }
  }
}

// v-model on a reactive() object can't reassign (const), so we
// manually merge incoming form edits into brandState. Object.assign
// keeps the same reactive reference + triggers reactivity.
function syncBrandState(incoming) {
  if (!incoming) return
  Object.assign(brandState, incoming)
}

function hydrateBrandStateFromDraft(d) {
  Object.assign(brandState, {
    name: d.name || '',
    theme: d.theme || '',
    style: d.style || '',
    mood: d.mood || '',
    color_palette: d.color_palette || '',
    motif: d.motif || '',
    era: d.era || '',
    energy: d.energy || '',
    generation_prompt_base: d.generation_prompt_base || '',
  })
}

async function refreshStatus() {
  if (!draft.value) return
  const s = await api.getCustomDeckStatus(draft.value.slug)
  if (s) {
    status.value = s
    if (s.draft) {
      draft.value = s.draft
      hydrateBrandStateFromDraft(s.draft)
    }
    const preview = await api.previewDeckTemplate(draft.value.slug)
    sampleCards.value = (preview?.cards || []).map(c => ({
      // BE returns Django values() notation. Two ID fields:
      //   ``id``   — TarotDeckTemplateCard.id (row in the join table)
      //   ``card`` — TarotCard.id (the canonical card FK we need
      //              when calling refresh-card)
      // Use the FK as card_id; it's what refresh-card expects.
      card_id: c.card || c.card_id,
      card_name: c.card__name || c.name || c.card_name,
      image_url: c.image_url || c.preview_url || '',
    }))
  }
}

function startPolling() {
  if (pollTimer) clearInterval(pollTimer)
  pollTimer = setInterval(async () => {
    await refreshStatus()
    if (status.value?.ready || draft.value?.creation_phase === 'approved') {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }, 5000)
}

function startSamplePolling() {
  if (pollTimer) clearInterval(pollTimer)
  pollTimer = setInterval(async () => {
    await refreshStatus()
    if (samplesComplete.value) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }, 4000)
}

// ── Actions ─────────────────────────────────────────────────────
async function onStart() {
  if (!brandState.name?.trim()) return
  starting.value = true
  startError.value = ''
  try {
    const r = await api.startCustomDeck({ ...brandState })
    if (r?.draft) {
      draft.value = r.draft
      hydrateBrandStateFromDraft(r.draft)
      await refreshStatus()
      // Auto-kick samples — operator filled the form, we know the
      // brief, no second click needed. Preview, ~2-4 min.
      await api.generateCustomDeckSamples(r.draft.slug).catch((e) => {
        generateError.value = e?.response?.data?.error || String(e)
      })
      startSamplePolling()
    }
  } catch (e) {
    const data = e?.response?.data
    if (data?.error === 'name_taken' && data.suggestion) {
      startError.value =
        `Name '${brandState.name}' is taken - try '${data.suggestion}'.`
    } else {
      startError.value = data?.message || data?.error || String(e)
    }
  } finally {
    starting.value = false
  }
}

async function onGenerateSamples() {
  if (!draft.value) return
  generatingSamples.value = true
  generateError.value = ''
  try {
    await api.generateCustomDeckSamples(draft.value.slug)
    startSamplePolling()
  } catch (e) {
    generateError.value = e?.response?.data?.error || String(e)
  } finally {
    generatingSamples.value = false
  }
}

async function onRefreshSamples() {
  // Bulk re-gen all 5 sample cards with the operator's edited
  // generation_prompt_base. Charges 100 ✦ (vs 5 x 50 ✦ per-card =
  // 125, so a 60% discount for refining everything together).
  if (!draft.value) return
  if (!brandState.generation_prompt_base?.trim()) return
  refreshingSamples.value = true
  refreshError.value = ''
  try {
    await api.refreshCustomDeckSamples(
      draft.value.slug, brandState.generation_prompt_base,
    )
    // Existing sample cards were deleted server-side; clear locally
    // so the placeholders reappear while the wrapper regenerates.
    sampleCards.value = []
    startSamplePolling()
  } catch (e) {
    const data = e?.response?.data
    if (data?.error === 'insufficient_carats') {
      refreshError.value =
        `Not enough carats. You have ${data.balance ?? 0}; refresh-all costs 100 carats.`
    } else {
      refreshError.value = data?.error || data?.message || String(e)
    }
  } finally {
    refreshingSamples.value = false
  }
}

async function onAbandon() {
  // Soft-archive: mark this draft as 'abandoned' so the in-flight
  // builder slot frees up. Cards stay in the DB and remain accessible
  // via the Mystic's gallery later. No emerald refund — sample gen
  // was free, so nothing to refund.
  if (!draft.value) return
  abandoning.value = true
  abandonError.value = ''
  try {
    await api.abandonCustomDeck(draft.value.slug)
    // Reset local state so we land back in no_draft (the form).
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
    draft.value = null
    sampleCards.value = []
    Object.assign(brandState, {
      name: '', theme: '', style: '', mood: '',
      color_palette: '', motif: '', era: '', energy: '',
      generation_prompt_base: '',
    })
  } catch (e) {
    abandonError.value = e?.response?.data?.error || String(e)
  } finally {
    abandoning.value = false
  }
}

async function onCommit() {
  if (!draft.value) return
  commitInProgress.value = true
  commitError.value = ''
  try {
    const r = await api.commitCustomDeck(draft.value.slug)
    if (r?.creation_phase) {
      draft.value = { ...draft.value, creation_phase: r.creation_phase }
      startPolling()
    }
  } catch (e) {
    const data = e?.response?.data
    if (data?.error === 'insufficient_carats') {
      commitError.value =
        `Not enough carats. You have ${data.balance ?? 0}; commit costs 1000 carats.`
    } else {
      commitError.value = data?.error || String(e)
    }
  } finally {
    commitInProgress.value = false
  }
}

// ── Aida chat-action hook (Phase 6 hand-off) ───────────────────
// Parent ReaderPage will dispatch a `fill-brand-fields` CustomEvent
// on this component (via a ref + .$emit-style pattern) when Aida
// returns ``{type: 'fill_brand_fields', fields: {...}}``. The chat
// integration on the ReaderPage side is the next slice.
function applyFillBrandFields(fields) {
  if (!fields || typeof fields !== 'object') return
  for (const [k, v] of Object.entries(fields)) {
    if (k in brandState && typeof v === 'string') {
      brandState[k] = v
    }
  }
}

// Expose for parent integration
defineExpose({ applyFillBrandFields, brandState })

// Auto-suggest Advanced when Aida fills any of the 5 hidden fields,
// so the operator sees what changed without manually toggling.
watch(
  () => [
    brandState.style, brandState.mood, brandState.color_palette,
    brandState.motif, brandState.era, brandState.energy,
  ],
  (newVals) => {
    if (newVals.some(v => (v || '').trim()) && formMode.value === 'simple') {
      // Don't auto-flip — operator may prefer Simple. Just leave hint.
      // (Future: surface an unobtrusive "Aida filled 5 more fields —
      // see Advanced tab" badge.)
    }
  },
)
</script>

<style scoped>
.custom-deck {
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 22px;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(195,169,255,0.12);
  border-radius: 14px;
}
.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.header-text { text-align: left; flex: 1; min-width: 0; }
.header-cta {
  flex-shrink: 0;
  align-self: center;
}
.title {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 18px;
  margin: 0 0 6px;
}
.sub {
  color: rgba(212, 196, 232, 0.7);
  font-size: 13px;
  margin: 0;
}

.mode-tabs {
  display: flex;
  gap: 0;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 10px;
  padding: 4px;
  width: fit-content;
}
.mode-tab {
  padding: 6px 18px;
  background: transparent;
  border: none;
  color: rgba(212, 196, 232, 0.6);
  font-family: 'Cinzel', serif;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  border-radius: 7px;
  transition: all 0.18s;
}
.mode-tab.active {
  background: rgba(201, 169, 78, 0.14);
  color: #C9A94E;
}
.mode-tab:not(.active):hover { color: rgba(212, 196, 232, 0.9); }

.summary-card {
  background: rgba(0, 0, 0, 0.32);
  border: 1px solid rgba(195, 169, 255, 0.15);
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.summary-head { display: flex; }
.summary-label {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(212, 196, 232, 0.55);
}
.summary-grid {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 4px 14px;
  margin: 0;
  font-size: 13px;
}
.summary-grid dt {
  color: rgba(212, 196, 232, 0.55);
  font-family: 'Cinzel', serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.summary-grid dd {
  color: #f3e9d2;
  margin: 0;
}
.summary-prompt {
  color: rgba(243, 233, 210, 0.85);
  font-size: 13px;
  line-height: 1.55;
  font-style: italic;
  margin: 4px 0 0;
  padding-top: 10px;
  border-top: 1px solid rgba(195, 169, 255, 0.1);
}

.loading-block {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  background: rgba(201, 169, 78, 0.06);
  border: 1px solid rgba(201, 169, 78, 0.22);
  border-radius: 12px;
}
.loading-title {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 13px;
  letter-spacing: 0.06em;
}
.loading-sub {
  color: rgba(212, 196, 232, 0.6);
  font-size: 12px;
  margin-top: 2px;
}

.copy {
  color: rgba(212, 196, 232, 0.85);
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}
.committed-note {
  font-size: 12px;
  color: rgba(212, 196, 232, 0.55);
  font-style: italic;
  margin-top: 12px;
}
.actions { display: flex; gap: 10px; flex-wrap: wrap; }
.primary-btn, .ghost-btn {
  padding: 10px 22px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.18s;
}
.primary-btn {
  background: #663399;
  color: #fff;
  border: none;
}
.primary-btn:hover:not(:disabled) { background: #7a3fb1; }
.primary-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.ghost-btn {
  background: transparent;
  border: 1px solid rgba(195,169,255,0.32);
  color: rgba(212, 196, 232, 0.85);
}
.ghost-btn:hover:not(:disabled) {
  border-color: #C9A94E;
  color: #C9A94E;
}

.adjust-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  background: rgba(0, 0, 0, 0.32);
  border: 1px solid rgba(195, 169, 255, 0.15);
  border-radius: 12px;
  margin-top: 12px;
}
.adjust-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.adjust-label > span {
  font-family: 'Cinzel', serif;
  font-size: 12px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #C9A94E;
}
.adjust-label > small {
  color: rgba(212, 196, 232, 0.6);
  font-size: 11px;
  font-style: italic;
}
.adjust-block textarea {
  background: rgba(0, 0, 0, 0.42);
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 8px;
  padding: 10px 12px;
  color: #f3e9d2;
  font-family: inherit;
  font-size: 13px;
  outline: none;
  resize: vertical;
  min-height: 88px;
}
.adjust-block textarea:focus { border-color: #C9A94E; }
.adjust-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.error.inline { margin: 0; }

.start-over-row {
  text-align: center;
  margin-top: 14px;
}
.start-over-link {
  background: transparent;
  border: none;
  color: rgba(212, 196, 232, 0.45);
  font-size: 11px;
  text-decoration: underline;
  cursor: pointer;
  font-family: inherit;
  padding: 6px 12px;
}
.start-over-link:hover:not(:disabled) {
  color: rgba(244, 67, 54, 0.85);
}
.start-over-link:disabled { opacity: 0.4; cursor: not-allowed; }

.commit-row { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
.commit-btn { align-self: flex-start; }
.commit-note {
  color: rgba(212, 196, 232, 0.55);
  font-size: 11px;
  font-style: italic;
  margin: 0;
}

.progress-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: rgba(201,169,78,0.1);
  border: 1px solid rgba(201,169,78,0.32);
  border-radius: 999px;
  width: fit-content;
}
.progress-label {
  font-family: 'Cinzel', serif;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #C9A94E;
}
.progress-count {
  font-size: 12px;
  color: rgba(212, 196, 232, 0.85);
}

.ready-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
  padding: 16px 18px;
  background: rgba(201, 169, 78, 0.08);
  border: 1px solid rgba(201, 169, 78, 0.32);
  border-radius: 12px;
  align-items: flex-start;
}
.ready-note {
  font-size: 12px;
  color: rgba(212, 196, 232, 0.7);
  font-style: italic;
  margin: 0;
}
.success {
  color: rgba(80, 200, 120, 0.95);
  font-size: 13px;
  margin: 4px 0 0;
}

.approved-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.approved-modal {
  max-width: 460px;
  width: 100%;
  background: linear-gradient(180deg, #1a0c2e 0%, #140a25 100%);
  border: 1px solid rgba(201, 169, 78, 0.55);
  border-radius: 14px;
  padding: 32px 28px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.6),
              0 0 32px rgba(201, 169, 78, 0.18);
}
.approved-icon {
  font-size: 36px;
  color: #C9A94E;
  filter: drop-shadow(0 0 12px rgba(201, 169, 78, 0.55));
}
.approved-modal h3 {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 22px;
  margin: 0;
  letter-spacing: 0.04em;
}
.approved-modal p {
  color: rgba(212, 196, 232, 0.85);
  font-size: 14px;
  line-height: 1.55;
  margin: 0;
}
.approved-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 8px;
  flex-wrap: wrap;
}

.loading {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(212, 196, 232, 0.6);
  font-size: 13px;
}
.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(195,169,255,0.18);
  border-top-color: #C9A94E;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.error {
  color: rgba(244, 67, 54, 0.9);
  font-size: 12px;
  margin: 4px 0 0;
}
</style>
